import { decodeJson, isRow, parseQuestions } from 'app/lib/forms/builder'
import type { AnswerValue, FormQuestion } from 'app/lib/forms/types'

type SentForm = { formId: string; formVersion: number; name: string; questions: FormQuestion[] }
export type SentPacket = { name: string; forms: SentForm[] }
export type PaperworkState = `sent` | `opened` | `completed` | `expired` | `cancelled`

/** The frozen copy of the packet taken at send time, so it reads as the patient saw it. */
export function parseSentPacket(value: unknown): SentPacket {
  const json = decodeJson(value)
  if (!isRow(json)) return { name: ``, forms: [] }
  const forms = Array.isArray(json.forms) ? json.forms.filter(isRow) : []
  return {
    name: typeof json.name === `string` ? json.name : ``,
    forms: forms
      .filter((form) => typeof form.formId === `string`)
      .map((form) => ({
        formId: form.formId as string,
        formVersion: typeof form.formVersion === `number` ? form.formVersion : 1,
        name: typeof form.name === `string` ? form.name : ``,
        questions: parseQuestions(form.questions),
      })),
  }
}

/** Answers keyed by form, then by question. */
export function parseSubmittedAnswers(value: unknown): Record<string, Record<string, AnswerValue>> {
  const json = decodeJson(value)
  if (!Array.isArray(json)) return {}
  return Object.fromEntries(
    json
      .filter(isRow)
      .filter((submission) => typeof submission.formId === `string` && Array.isArray(submission.answers))
      .map((submission) => [
        submission.formId as string,
        Object.fromEntries(
          (submission.answers as unknown[])
            .filter(isRow)
            .filter((answer) => typeof answer.questionId === `string`)
            .map((answer) => [answer.questionId as string, answer.value as AnswerValue]),
        ),
      ]),
  )
}

/** Readable answer text. Choices are stored by option id, so they are turned back into labels. */
export function answerText(question: FormQuestion, value: AnswerValue | undefined): string {
  if (value === undefined || value === `` || (Array.isArray(value) && value.length === 0)) return `No answer`
  const values = Array.isArray(value) ? value : [value]
  if (question.kind === `yesNo`) return values[0] === `yes` ? `Yes` : `No`
  if (question.options) {
    return values.map((id) => question.options?.find((option) => option.id === id)?.label ?? id).join(`, `)
  }
  return values.join(`, `)
}

/** The API only marks a link expired when someone opens it, so an unopened old link still says sent. */
export function paperworkState(record: { status: string; expiresDate: string }, now: Date): PaperworkState {
  const open = record.status === `sent` || record.status === `opened`
  if (open && new Date(record.expiresDate) < now) return `expired`
  return record.status as PaperworkState
}

export type PatientChoice = {
  id: string
  pmsPatientId?: string | null
  firstName?: string | null
  lastName?: string | null
  preferredName?: string | null
  birthDate?: string | null
  status?: string | null
  lastVisit?: string | null
}

const DAY = { month: `short`, day: `numeric`, year: `numeric`, timeZone: `UTC` } as const

export function patientName(patient: PatientChoice): string {
  const first = patient.preferredName || patient.firstName
  return [first, patient.lastName].filter(Boolean).join(` `) || `Unnamed patient`
}

/** Everything that tells two same-named records apart: a family on one phone, or PMS duplicates. */
export function patientDetails(patient: PatientChoice): string {
  const status = patient.status ? patient.status.charAt(0) + patient.status.slice(1).toLowerCase() : undefined
  return [
    patient.birthDate && `Born ${new Date(patient.birthDate).toLocaleDateString(`en-US`, DAY)}`,
    patient.pmsPatientId && `PMS #${patient.pmsPatientId}`,
    status,
    patient.lastVisit && `Last visit ${new Date(patient.lastVisit).toLocaleDateString(`en-US`, DAY)}`,
  ]
    .filter(Boolean)
    .join(` · `)
}
